import { Component, EventEmitter, inject, OnDestroy } from '@angular/core';
import {
	FormularioRelatorio,
	INPUTS_RELATORIOS,
	InstanciaRelatorio,
} from '@home/submodulos/dados-meteorologicos/interfaces/tipos-relatorios';
import * as Highcharts from 'highcharts/highstock';

import { Validators } from '@angular/forms';
import {
	optionsChartClimatologia,
	relatorioClimatologia,
} from '@home/submodulos/dados-meteorologicos/componentes/relatorios/grafico-climatologia/chart-options';
import { ClimatologiaResponseDTO } from '@home/submodulos/dados-meteorologicos/interfaces/climatologia';
import { RelatoriosService } from '@home/submodulos/dados-meteorologicos/services/relatorios.service';

import {
	COR_REGIAO_INDIVIDUAL,
	corrigeDuplicacaoNome,
	gerarFilenameGrafico,
} from '../../../utils';
import {
	alinharDado,
	baixarEAbrirPDF,
	capitalizeFirstLetter,
	compararStrings,
	enumAsSelectOptions,
	isNuloOuUndefined,
	numberToBrNumberTables,
	verificaSePDFEAdicionaNaTabela,
} from '@utils';
import { Chart } from 'highcharts';
import { Subject, Subscription, takeUntil } from 'rxjs';
import * as pdfseira from '@utils/pdf-seira';
import { DocumentExporter } from '@utils/document-exporter';
import * as ExcelTable from 'mr-excel';
import { DataModel } from 'mr-excel';
import { GroupButton } from '@componentes/public-button-group/public-button-group.component';
import moment from 'moment/moment';
import html2canvas from 'html2canvas';
import { Agrupamento, AgrupamentoEspecial, Meses } from '../../../interfaces';
import { Select } from '@layout/interfaces/select';
import { NumberUtils } from '@utils/number-utils';
import {
	getCorPorRegiao,
	ordenarRegioesPluviometricas,
} from '../../../utils/grafico-regioes-utils';
import { isEstadoCompleto } from '../../../utils/agrupamento-utils';

@Component({
	selector: 'seira-climatologia-grafico',
	templateUrl: './grafico-climatologia.component.html',
	styleUrls: ['./grafico-climatologia.component.scss'],
})
export class GraficoClimatologiaComponent
	implements OnDestroy, InstanciaRelatorio
{
	inputs = inject(INPUTS_RELATORIOS);
	chart?: Highcharts.Chart;
	resultado: ClimatologiaResponseDTO[] = [];
	loading = true;
	gerou = false;
	private subscription = new Subscription();
	_destroyed = new Subject();
	tipoAgrupamento = 'MUNICIPIO_POSTO';
	descricaoRelatorio =
		'Define-se como climatologia pluviométrica, a média histórica de pluviometria para períodos longos, preferencialmente de 30 anos.';

	height = '190px';
	chartInstance = new EventEmitter<Chart>();
	chartOptions = optionsChartClimatologia();

	series: Array<Partial<Highcharts.SeriesOptionsType> & Record<string, any>> =
		relatorioClimatologia;

	periodoTitulo = '';

	tituloAgrupamento = '';
	agrpamentoSelecionado = '';
	botoesDeExportacao: GroupButton[] = [
		{
			label: '.png',
			size: 'small',
			icon: 'ph-image',
			onClick: () => this.exportarPNG(),
		},
		{
			label: '.pdf',
			size: 'small',
			icon: 'ph-file-pdf',
			onClick: () => {
				return this.exportarPDF();
			},
		},
		{
			label: '.csv',
			size: 'small',
			icon: 'ph-file-csv',
			onClick: () => this.exportarCSV(),
		},
		{
			label: '.txt',
			size: 'small',
			icon: 'ph-file-text',
			onClick: () => this.exportarTXT(),
		},
		{
			label: '.xlsx',
			size: 'small',
			icon: 'ph-file-xls',
			onClick: () => this.exportarXLSX(),
		},
	];
	mesesDoAno: Record<string, number> = {
		janeiro: 1,
		fevereiro: 2,
		marco: 3,
		abril: 4,
		maio: 5,
		junho: 6,
		julho: 7,
		agosto: 8,
		setembro: 9,
		outubro: 10,
		novembro: 11,
		dezembro: 12,
	};

	agrupamentoSelecionado = '';
	agrupamento: Select[] | any = [];
	get _agrupamento(): string {
		return this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.value;
	}

	get isMicrorregiaoEstadoCompleto() {
		return (
			this._agrupamento === 'MICRORREGIAO' &&
			this.inputs.form
				.get(FormularioRelatorio.MICRORREGIAO)
				?.value?.toString() === '0'
		);
	}

	get isMesorregiaoEstadoCompleto() {
		return (
			this._agrupamento === 'MESORREGIAO' &&
			this.inputs.form
				.get(FormularioRelatorio.MESORREGIAO)
				?.value?.toString() === '0'
		);
	}

	get isBaciaEstadoCompleto() {
		return (
			this._agrupamento === 'BACIA_SUBBACIA' &&
			this.inputs.form.get(FormularioRelatorio.BACIA)?.value?.toString() === '0'
		);
	}

	get isRegiaoEstadoCompleto() {
		return (
			this._agrupamento === 'REGIAO_PLUVIOMETRICA' &&
			this.inputs.form
				.get(FormularioRelatorio.REGIAO_PLUVIOMETRICA)
				?.value?.toString() === '0'
		);
	}

	constructor(private climatologiaService: RelatoriosService) {
		this.setValidators();
		this.agrupamento = enumAsSelectOptions(AgrupamentoEspecial);
	}

	ngOnDestroy() {
		this.inputs.form.get(FormularioRelatorio.MES_INICIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MES_FIM)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.ESTACAO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MUNICIPIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MICRORREGIAO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MESORREGIAO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.BACIA)?.clearValidators();
		this.inputs.form
			.get(FormularioRelatorio.REGIAO_PLUVIOMETRICA)
			?.clearValidators();

		this.subscription.unsubscribe();
		this._destroyed.complete();
		this.chartInstance.complete();
		this.resetarGrafico();
	}

	resetarGrafico() {
		if (this.chart) {
			while (this.chart.series?.length) {
				this.chart.series[0].remove(false);
			}
			this.chart = undefined;
		}
		this.series = [...relatorioClimatologia];
		this.chartOptions = optionsChartClimatologia();
	}

	retornarMesInicioMesFinalFormatado() {
		const mesInicio = this.inputs.form.get(
			FormularioRelatorio.MES_INICIO
		)?.value;
		const mesFim = this.inputs.form.get(FormularioRelatorio.MES_FIM)?.value;

		if (!mesInicio || !mesFim) {
			return;
		}

		const mesInicioFormatado = Object.values(Meses).find(mes =>
			compararStrings(mes, mesInicio)
		);
		const mesFimFormatado = Object.values(Meses).find(mes =>
			compararStrings(mes, mesFim)
		);
		this.periodoTitulo = ` ${mesInicioFormatado} a ${mesFimFormatado}`;
	}

	getNomeRegiaoPluviometricaSelecionada() {
		const regiaoSelecionada = this.inputs.form.get(
			FormularioRelatorio.REGIAO_PLUVIOMETRICA
		)?.value;

		return this.inputs.regioes.find(
			regiao => regiao.value?.toString() === regiaoSelecionada?.toString()
		)?.name;
	}

	getNomeMicrorregiaoSelecionada() {
		const microrregiaoSelecionada = this.inputs.form.get(
			FormularioRelatorio.MICRORREGIAO
		)?.value;

		return this.inputs.microrregioes.find(
			microrregiao =>
				microrregiao.value?.toString() === microrregiaoSelecionada?.toString()
		)?.name;
	}

	getNomeMesorregiaoSelecionada() {
		const mesorregiaoSelecionada = this.inputs.form.get(
			FormularioRelatorio.MESORREGIAO
		)?.value;

		return this.inputs.mesorregioes.find(
			mesorregiao =>
				mesorregiao.value?.toString() === mesorregiaoSelecionada?.toString()
		)?.name;
	}

	getNomeBaciaSelecionada() {
		const baciaSelecionada = this.inputs.form.get(
			FormularioRelatorio.BACIA
		)?.value;

		return this.inputs.bacias.find(
			bacia => bacia.value?.toString() === baciaSelecionada?.toString()
		)?.name;
	}

	gerarRelatorio(): void {
		if (this.inputs.form.invalid) {
			return;
		}

		this.resetarGrafico();

		const mesInicio = this.inputs.form.get(
			FormularioRelatorio.MES_INICIO
		)?.value;
		const mesFim = this.inputs.form.get(FormularioRelatorio.MES_FIM)?.value;

		if (!mesInicio || !mesFim) {
			return;
		}

		this.loading = true;
		this.inputs.setLoading(true);
		this.tipoAgrupamento = this.inputs.form.get('agrupamento')?.value;
		const idMunicipioSelecionado = this.inputs.form.get('municipio')?.value;
		const idMicrorregiaoSelecionada =
			this.inputs.form.get('microrregiao')?.value;
		const idMesorregiaoSelecionada = this.inputs.form.get('mesorregiao')?.value;
		const idBaciaSelecionada = this.inputs.form.get('bacia')?.value;
		const idMunicipio =
			this.tipoAgrupamento === 'MUNICIPIO' ? idMunicipioSelecionado : undefined;
		const idMicrorregiao =
			this.tipoAgrupamento === 'MICRORREGIAO' &&
			idMicrorregiaoSelecionada?.toString() === '0'
				? undefined
				: idMicrorregiaoSelecionada;
		const idMesorregiao =
			this.tipoAgrupamento === 'MESORREGIAO' &&
			idMesorregiaoSelecionada?.toString() === '0'
				? undefined
				: idMesorregiaoSelecionada;
		const idBacia =
			this.tipoAgrupamento === 'BACIA_SUBBACIA' &&
			idBaciaSelecionada?.toString() === '0'
				? undefined
				: idBaciaSelecionada;
		const idPosto =
			this.tipoAgrupamento === 'MUNICIPIO_POSTO'
				? this.inputs.form.get('estacao')?.value
				: undefined;
		const idRegiao = this.inputs.form.get(
			FormularioRelatorio.REGIAO_PLUVIOMETRICA
		)?.value;
		const idRegiaoSelecionada =
			this.tipoAgrupamento === 'REGIAO_PLUVIOMETRICA' &&
			idRegiao?.toString() === '0'
				? undefined
				: idRegiao;

		this.retornarMesInicioMesFinalFormatado();

		this.climatologiaService
			.buscarClimatologias(
				mesInicio.toLowerCase(),
				mesFim.toLowerCase(),
				this.tipoAgrupamento,
				idMunicipio,
				idPosto,
				idMicrorregiao,
				idMesorregiao,
				idBacia,
				idRegiaoSelecionada
			)
			.subscribe({
				next: climatologias => {
					const nomeRegiaoSelecionada =
						this.tipoAgrupamento === 'REGIAO_PLUVIOMETRICA' &&
						idRegiao &&
						idRegiao !== '0'
							? this.getNomeRegiaoPluviometricaSelecionada()
							: undefined;
					const nomeMicrorregiaoSelecionada =
						this.tipoAgrupamento === 'MICRORREGIAO' &&
						idMicrorregiaoSelecionada &&
						idMicrorregiaoSelecionada !== '0'
							? this.getNomeMicrorregiaoSelecionada()
							: undefined;
					const nomeMesorregiaoSelecionada =
						this.tipoAgrupamento === 'MESORREGIAO' &&
						idMesorregiaoSelecionada &&
						idMesorregiaoSelecionada !== '0'
							? this.getNomeMesorregiaoSelecionada()
							: undefined;
					const nomeBaciaSelecionada =
						this.tipoAgrupamento === 'BACIA_SUBBACIA' &&
						idBaciaSelecionada &&
						idBaciaSelecionada !== '0'
							? this.getNomeBaciaSelecionada()
							: undefined;
					const climatologiasFiltradas = nomeRegiaoSelecionada
						? climatologias.filter(
								item =>
									corrigeDuplicacaoNome(item.nome) ===
									corrigeDuplicacaoNome(nomeRegiaoSelecionada)
							)
						: nomeMicrorregiaoSelecionada
							? climatologias.filter(
									item =>
										corrigeDuplicacaoNome(item.nome) ===
										corrigeDuplicacaoNome(nomeMicrorregiaoSelecionada)
								)
							: nomeMesorregiaoSelecionada
								? climatologias.filter(
										item =>
											corrigeDuplicacaoNome(item.nome) ===
											corrigeDuplicacaoNome(nomeMesorregiaoSelecionada)
									)
								: nomeBaciaSelecionada
									? climatologias.filter(
											item =>
												corrigeDuplicacaoNome(item.nome) ===
												corrigeDuplicacaoNome(nomeBaciaSelecionada)
										)
									: climatologias;

					this.climatologiaService.atualizaVisibilidadeBotoesExportacao(
						climatologiasFiltradas
					);
					this.resultado = climatologiasFiltradas;

					this.resultado = this.ordenarPorMes(this.resultado);

					this.chartOptions = {
						...this.chartOptions,
						exporting: {
							enabled: false, // desativa export pelo botão do Highcharts
							buttons: {
								contextButton: {
									enabled: false, // remove o botão sanduiche
								},
							},
							filename: gerarFilenameGrafico(
								`grafico_climatologia_${this.inputs.form
									.get(FormularioRelatorio.AGRUPAMENTO)
									?.value.toLocaleLowerCase()}`
							),
						},
					};
				},
				error: () => {
					this.inputs.setLoading(false);
					this.loading = false;
					this.gerou = false;
				},
				complete: () => {
					this.inputs.setLoading(false);
					this.loading = false;
					this.gerou = true;
				},
			});
	}

	onChartCreated(chart: Chart) {
		if (this.resultado) {
			this.loading = false;
			this.chart = chart;
			this.chartInstance.emit(chart);

			this.setSeries();
		}
	}

	lidarComAgrupamento() {
		const agrupamento = this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO);
		const municipio = this.inputs.form.get(FormularioRelatorio.MUNICIPIO);
		const microrregiao = this.inputs.form.get(FormularioRelatorio.MICRORREGIAO);
		const mesorregiao = this.inputs.form.get(FormularioRelatorio.MESORREGIAO);
		const bacia = this.inputs.form.get(FormularioRelatorio.BACIA);
		const municipioPosto = this.inputs.form.get(FormularioRelatorio.ESTACAO);
		const regiaoPluviometrica = this.inputs.form.get(
			FormularioRelatorio.REGIAO_PLUVIOMETRICA
		);

		municipio?.clearValidators();
		microrregiao?.clearValidators();
		mesorregiao?.clearValidators();
		bacia?.clearValidators();
		municipioPosto?.clearValidators();
		regiaoPluviometrica?.clearValidators();

		if (agrupamento?.value !== 'MUNICIPIO') {
			municipio?.setValue(null, { emitEvent: false });
		}
		if (agrupamento?.value !== 'MICRORREGIAO') {
			microrregiao?.setValue(null, { emitEvent: false });
		}
		if (agrupamento?.value !== 'MESORREGIAO') {
			mesorregiao?.setValue(null, { emitEvent: false });
		}
		if (agrupamento?.value !== 'BACIA_SUBBACIA') {
			bacia?.setValue(null, { emitEvent: false });
		}
		if (agrupamento?.value !== 'MUNICIPIO_POSTO') {
			municipioPosto?.setValue(null, { emitEvent: false });
		}
		if (agrupamento?.value !== 'REGIAO_PLUVIOMETRICA') {
			regiaoPluviometrica?.setValue(null, { emitEvent: false });
		}

		switch (agrupamento?.value) {
			case 'MUNICIPIO_POSTO':
				if (!municipioPosto?.value) {
					municipioPosto?.setValue(this.inputs.municipioPostos?.[0]?.value, {
						emitEvent: false,
					});
				}
				municipioPosto?.setValidators(Validators.required);
				break;
			case 'MICRORREGIAO':
				microrregiao?.setValue('0', { emitEvent: false });
				microrregiao?.setValidators(Validators.required);
				break;
			case 'MESORREGIAO':
				mesorregiao?.setValue('0', { emitEvent: false });
				mesorregiao?.setValidators(Validators.required);
				break;
			case 'BACIA_SUBBACIA':
				bacia?.setValue('0', { emitEvent: false });
				bacia?.setValidators(Validators.required);
				break;
			case 'MUNICIPIO':
				municipio?.setValidators(Validators.required);
				break;
			case 'REGIAO_PLUVIOMETRICA':
				regiaoPluviometrica?.setValue('0', { emitEvent: false });
				regiaoPluviometrica?.setValidators(Validators.required);
				break;
		}

		municipio?.updateValueAndValidity({ emitEvent: false });
		microrregiao?.updateValueAndValidity({ emitEvent: false });
		mesorregiao?.updateValueAndValidity({ emitEvent: false });
		bacia?.updateValueAndValidity({ emitEvent: false });
		municipioPosto?.updateValueAndValidity({ emitEvent: false });
		regiaoPluviometrica?.updateValueAndValidity({ emitEvent: false });
	}

	setValidators() {
		this.lidarComAgrupamento();
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.valueChanges.pipe(takeUntil(this._destroyed))
			.subscribe({
				next: () => {
					this.lidarComAgrupamento();
				},
			});
		this.inputs.form
			.get(FormularioRelatorio.MES_INICIO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.MES_FIM)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.setValidators(Validators.required);
	}

	private getCorSerie(nomeSerie: string): string | undefined {
		if (!isEstadoCompleto(this.inputs.form, this._agrupamento)) {
			return COR_REGIAO_INDIVIDUAL;
		}
		if (this._agrupamento === 'MESORREGIAO') {
			return getCorPorRegiao(
				nomeSerie,
				this.inputs.mesorregioes,
				this._agrupamento
			);
		}
		if (this._agrupamento === 'REGIAO_PLUVIOMETRICA') {
			return getCorPorRegiao(nomeSerie, this.inputs.regioes, this._agrupamento);
		}

		return undefined;
	}

	setSeries() {
		this.chart?.series.forEach(el => el.remove());
		this.series?.map(serie => {
			serie.data = [];
			return serie;
		});

		this.resultado = this.ordenarPorMes(this.resultado);
		const meses: string[] = this.extrairMeses();
		const agrupamentoUnicoGrafico = ['MUNICIPIO', 'MUNICIPIO_POSTO'];

		const exibeGraficoPorAgrupamentoUnico =
			agrupamentoUnicoGrafico.includes(<Agrupamento>this.tipoAgrupamento) ||
			(this.tipoAgrupamento === 'MICRORREGIAO' &&
				!this.isMicrorregiaoEstadoCompleto) ||
			(this.tipoAgrupamento === 'MESORREGIAO' &&
				!this.isMesorregiaoEstadoCompleto) ||
			(this.tipoAgrupamento === 'BACIA_SUBBACIA' &&
				!this.isBaciaEstadoCompleto) ||
			(this.tipoAgrupamento === 'REGIAO_PLUVIOMETRICA' &&
				!this.isRegiaoEstadoCompleto);

		const mesInicio = this.inputs.form.get(
			FormularioRelatorio.MES_INICIO
		)?.value;
		const mesFim = this.inputs.form.get(FormularioRelatorio.MES_FIM)?.value;

		const mesInicioFormatado = this.verificaMes(mesInicio.toLowerCase());
		const mesFimFormatado = this.verificaMes(mesFim.toLowerCase());

		const periodoExibido =
			mesInicioFormatado === mesFimFormatado
				? capitalizeFirstLetter(mesInicioFormatado)
				: `${capitalizeFirstLetter(
						mesInicioFormatado
					)} a ${capitalizeFirstLetter(mesFimFormatado)}`;

		if (exibeGraficoPorAgrupamentoUnico) {
			const nomeAgrupamento = this.resultado[0]?.nome
				? this.resultado[0]?.nome
				: '-';
			const nomeMunicipio =
				this.tipoAgrupamento === 'MUNICIPIO_POSTO' && this.resultado[0]
					? this.resultado[0]?.dadosPosto?.municipio
					: '';

			const nomeFinal =
				this.tipoAgrupamento.toString() === 'MUNICIPIO_POSTO'
					? corrigeDuplicacaoNome(`${nomeMunicipio}/${nomeAgrupamento}`)
					: nomeAgrupamento;

			this.chart?.setTitle({
				text: `Climatologia (mm) de ${nomeFinal} - ${periodoExibido}`,
				align: 'center',
			});

			this.resultado.forEach(value => {
				this.series[0].data.push({
					y: Number.parseFloat(value?.climatologia.toFixed(1)),
					name: capitalizeFirstLetter(this.verificaMes(value.mes)),
				});
			});

			this.chart!.xAxis[0].setCategories(meses);
			this.series.forEach(el => {
				this.chart?.addSeries(el as any);
			});
		} else {
			this.configurarChart(this.resultado);
		}
	}
	configurarChart(preciptacoesAgrupadas: ClimatologiaResponseDTO[]) {
		let categorias = this.extrairNomes(preciptacoesAgrupadas);
		const meses: string[] = this.extrairMeses();

		if (this.tipoAgrupamento === 'REGIAO_PLUVIOMETRICA') {
			categorias = ordenarRegioesPluviometricas(categorias);
		}

		this.chart?.xAxis[0].setCategories(meses);
		this.chart?.setTitle({
			text: `Gráfico de Climatologia (mm)`,
			align: 'center',
		});
		this.series = categorias.map(res => {
			return {
				name: res,
				color: this.getCorSerie(res),
				data: meses.map(mes => {
					const entry = this.resultado.find(
						item =>
							item.nome === res &&
							capitalizeFirstLetter(this.verificaMes(item.mes)) === mes
					);

					return entry
						? {
								name: this.verificaMes(entry.mes),
								y: Number.parseFloat(entry?.climatologia.toFixed(1)),
							}
						: null;
				}),
			};
		});

		this.series.forEach(el => {
			this.chart?.addSeries(el as any);
		});
	}
	verificaMes(mes: string) {
		const retorno = mes === 'marco' ? 'Março' : capitalizeFirstLetter(mes);
		return retorno;
	}

	extrairNomes(preciptacoesAgrupadas: ClimatologiaResponseDTO[]) {
		return [...new Set(preciptacoesAgrupadas.map(el => el.nome))].sort();
	}

	extrairMeses() {
		return [
			...new Set(
				this.resultado.map((item: { mes: string }) => {
					if (item.mes === 'marco') {
						return capitalizeFirstLetter('março');
					} else {
						return capitalizeFirstLetter(item.mes);
					}
				})
			),
		];
	}

	ordenarPorMes(
		resultados: ClimatologiaResponseDTO[]
	): ClimatologiaResponseDTO[] {
		return resultados.sort((a, b) => {
			const indiceA = this.mesesDoAno[a.mes.toLowerCase()];
			const indiceB = this.mesesDoAno[b.mes.toLowerCase()];
			return indiceA - indiceB;
		});
	}

	getDataFormatada(data: Date): string {
		return moment(data).format('DD/MM/YYYY');
	}
	private carregarImagem(src: string): Promise<HTMLImageElement> {
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.crossOrigin = 'anonymous';
			img.onload = () => resolve(img);
			img.onerror = err => reject(err);
			img.src = src;
		});
	}

	private async getLogoBase64(caminho: string): Promise<string | null> {
		try {
			const logo = await this.carregarImagem(caminho);
			const canvas = document.createElement('canvas');
			canvas.width = logo.width;
			canvas.height = logo.height;
			const ctx = canvas.getContext('2d')!;
			ctx.drawImage(logo, 0, 0);
			return canvas.toDataURL('image/png');
		} catch (e) {
			console.error(`[CLIMATOLOGIA] Erro ao carregar logo ${caminho}:`, e);
			return null;
		}
	}

	async getMapImage() {
		const elementoHtml = document.getElementById('grafico-climatologia');
		if (elementoHtml) {
			const canva = await html2canvas(elementoHtml, {
				useCORS: true,
				allowTaint: true,
				logging: false,
				scale: 2,
			});

			return canva.toDataURL('image/png', 1);
		} else {
			return null;
		}
	}

	getDadosTabelaParaExportacao(
		dados: ClimatologiaResponseDTO[],
		isPdf: boolean
	) {
		const tableData: any[][] = [];
		const colunas: any[] = [];
		const agrupamento = this.inputs.form.get('agrupamento')?.value;

		colunas.push(
			{
				text: AgrupamentoEspecial[
					agrupamento as keyof typeof AgrupamentoEspecial
				],
				fillColor: '#DCDCDC',
				alignment: 'center',
			},
			{ text: 'Mês', fillColor: '#DCDCDC', alignment: 'center' },
			{ text: 'Climatologia (mm)', fillColor: '#DCDCDC', alignment: 'center' }
		);

		verificaSePDFEAdicionaNaTabela(tableData, colunas, isPdf);

		dados.forEach((item: ClimatologiaResponseDTO) => {
			const rowData = [];
			if (item.mes === 'marco') item.mes = item.mes.replace('c', 'ç');

			rowData.push(
				agrupamento === 'MUNICIPIO_POSTO'
					? item.nome
					: alinharDado(item.nome, isPdf),
				alinharDado(capitalizeFirstLetter(item.mes), isPdf),
				alinharDado(numberToBrNumberTables(item.climatologia, 1), isPdf)
			);
			tableData.push(rowData);
		});

		return tableData;
	}

	retornarClimatologiaParaXLSX() {
		const tableData: any[] = [];

		this.resultado.map(({ nome, mes, climatologia }) => {
			if (mes === 'marco') mes = mes.replace('c', 'ç');

			tableData.push({
				nome: nome,
				mes: capitalizeFirstLetter(mes),
				climatologia: isNuloOuUndefined(climatologia)
					? '-'
					: NumberUtils.getIgnorandoCasasDecimais(climatologia),
			});
		});

		return tableData;
	}

	async exportarPDF() {
		const documentDefinition: any =
			await pdfseira.documentDefinitions('landscape');
		const img = await this.getMapImage();

		const logoAesaBase64 = await this.getLogoBase64(
			'assets/images/logos/logoAesa.svg'
		);
		const logoGovernoBase64 = await this.getLogoBase64(
			'assets/images/logos/governo_pb.png'
		);

		if (logoAesaBase64 || logoGovernoBase64) {
			documentDefinition.images = documentDefinition.images || {};

			if (logoAesaBase64) {
				documentDefinition.images['aesaLogo'] = logoAesaBase64;
			}

			if (logoGovernoBase64) {
				documentDefinition.images['governoLogo'] = logoGovernoBase64;
			}

			documentDefinition.footer = (currentPage: number, pageCount: number) => {
				return {
					margin: [40, 0, 40, 20],
					columns: [
						{
							image: 'aesaLogo',
							width: 70,
							alignment: 'left',
						},
						{
							text: `Página ${currentPage} de ${pageCount}`,
							alignment: 'center',
							fontSize: 8,
							width: '*',
						},
						{
							image: 'governoLogo',
							width: 70,
							alignment: 'right',
						},
					],
				};
			};
		}

		documentDefinition.content.push([
			{
				image: img,
				width: 590,
				height: 390,
				margin: [0, 45, 5, 15],
				alignment: 'center',
			},
		]);

		const tableData = this.getDadosTabelaParaExportacao(this.resultado, true);

		documentDefinition.content.push(
			{
				text: '',
				alignment: 'center',
				fontSize: 10,
			},
			{
				table: {
					widths: ['*', '*', '*'],
					body: tableData,
				},
			}
		);

		baixarEAbrirPDF(
			`Climatologia (mm) - ${this.periodoTitulo}`,
			documentDefinition
		);
	}

	exportarCSV() {
		const tableData = this.getDadosTabelaParaExportacao(this.resultado, false);

		DocumentExporter.gerarCSV(
			tableData,
			`Climatologia (mm) - ${this.periodoTitulo}`
		);
	}

	exportarTXT() {
		const tableData = this.getDadosTabelaParaExportacao(this.resultado, false);

		let txtData = '';
		if (tableData.length > 0) {
			const colunas = tableData[0];

			tableData.slice(1).forEach(dadoRelatorio => {
				colunas.forEach((coluna: string, index: number) => {
					const valor = dadoRelatorio[index];
					txtData += `${coluna}: ${valor ?? '-'}\n`;
				});

				txtData += '\n';
			});
		}

		DocumentExporter.gerarTXT(
			txtData,
			`Climatologia (mm) - ${this.periodoTitulo}`
		);
	}

	exportarXLSX() {
		const agrupamento = this.inputs.form.get('agrupamento')?.value;
		const nomeArquivo = `Climatologia (mm) - ${this.periodoTitulo}`;
		const colorPalette = {
			c1: '2C3639',
			c2: 'FFFFFF',
			c3: '000000',
			c4: 'EEEEEE',
		};
		const rowStyle = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
		};
		const headerStyle = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
		};
		const headerStyleCenter = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as DataModel.AlignmentOption,
		};
		const rowStyleCenter = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as DataModel.AlignmentOption,
		};
		const title = {
			backgroundColor: colorPalette.c2,
			whiteSpace: 'pre',
			color: colorPalette.c3,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
				wrapText: 1,
			} as DataModel.AlignmentOption,
		};
		const headers = [
			{
				label: 'nome',
				text:
					agrupamento === 'MUNICIPIO_POSTO'
						? 'Município/Posto'
						: agrupamento === 'MUNICIPIO'
							? 'Município'
							: agrupamento === 'MICRORREGIAO'
								? 'Microrregião'
								: agrupamento === 'MESORREGIAO'
									? 'Mesorregião'
									: agrupamento === 'BACIA_SUBBACIA'
										? 'Bacia/Sub-bacia'
										: agrupamento === 'REGIAO_PLUVIOMETRICA'
											? 'Região pluviométrica'
											: '',
				size: 24,
			},
			{
				label: 'mes',
				text: 'Mês',
				size: 48,
			},
			{ label: 'climatologia', text: 'Climatologia (mm)', size: 24 },
		];
		const dataExcel = {
			styles: {
				headerStyle,
				headerStyleCenter,
				rowStyle,
				rowStyleCenter,
				title,
			},
			sheet: [
				{
					shiftTop: 3,
					images: [
						{
							url: 'assets/images/cabecalho/cabeçalho_excel.png',
							from: 'A1',
							to: 'C3',
						},
					],
					styleCellCondition(
						data: any,
						object: any,
						rowIndex: number,
						colIndex: number,
						fromHeader: boolean,
						styleKeys: string[]
					) {
						if (data.label) {
							return 'headerStyleCenter';
						} else {
							if (colIndex === 0 && agrupamento === 'MUNICIPIO_POSTO') {
								return 'rowStyle';
							} else {
								return 'rowStyleCenter';
							}
						}
					},
					headers: headers,
					data: this.retornarClimatologiaParaXLSX(),
					columns: [
						{ key: 'mes' },
						{
							key: 'climatologia',
							style: { numFmt: '0.0' },
						},
					],
					title: {
						consommeRow: 3,
						consommeCol: 3,
						text: nomeArquivo,
						styleId: 'title',
						alignment: 'center',
					},
				},
			],
			fileName: nomeArquivo,
		};

		ExcelTable.generateExcel(dataExcel);
	}
	async exportarPNG() {
		try {
			const baseImgDataUrl = await this.getMapImage();
			if (!baseImgDataUrl) {
				console.error('Não foi possível capturar o gráfico para PNG.');
				return;
			}

			const baseImg = await this.carregarImagem(baseImgDataUrl);

			const alturaRodape = Math.max(baseImg.height * 0.1, 70);

			const canvas = document.createElement('canvas');
			canvas.width = baseImg.width;
			canvas.height = baseImg.height + alturaRodape;

			const ctx = canvas.getContext('2d')!;
			ctx.fillStyle = '#FFFFFF';
			ctx.fillRect(0, 0, canvas.width, canvas.height);

			ctx.drawImage(baseImg, 0, 0, baseImg.width, baseImg.height);

			try {
				const margem = Math.max(canvas.width * 0.02, 12);

				const larguraLogoAesa = Math.max(canvas.width * 0.08, 80);
				const larguraLogoGoverno = Math.max(canvas.width * 0.1, 110);

				const logoAesa = await this.carregarImagem(
					'assets/images/logos/logoAesa.svg'
				);
				const logoGoverno = await this.carregarImagem(
					'assets/images/logos/governo_pb.png'
				);

				const alturaLogoAesa =
					(larguraLogoAesa / logoAesa.width) * logoAesa.height;

				const alturaLogoGoverno =
					(larguraLogoGoverno / logoGoverno.width) * logoGoverno.height;

				const yLogoAesa = baseImg.height + (alturaRodape - alturaLogoAesa) / 2;
				const yLogoGoverno =
					baseImg.height + (alturaRodape - alturaLogoGoverno) / 2;

				ctx.drawImage(
					logoAesa,
					margem,
					yLogoAesa,
					larguraLogoAesa,
					alturaLogoAesa
				);

				ctx.drawImage(
					logoGoverno,
					canvas.width - larguraLogoGoverno - margem,
					yLogoGoverno,
					larguraLogoGoverno,
					alturaLogoGoverno
				);
			} catch (e) {
				console.error('Erro ao desenhar logos no PNG:', e);
			}

			const finalDataUrl = canvas.toDataURL('image/png', 1);

			const link = document.createElement('a');
			link.href = finalDataUrl;
			link.download = `Climatologia (mm) - ${this.periodoTitulo}.png`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} catch (e) {
			console.error('Erro ao exportar PNG:', e);
		}
	}
}
